import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, TrendingUp } from 'lucide-react';
import api from '../lib/api';

interface Protocol {
  id: number;
  name: string;
  slug: string;
  network: string;
  category: string;
  apy: number;
}

export default function ProtocolSearch() {
  const [query, setQuery] = useState('');
  const [protocols, setProtocols] = useState<Protocol[]>([]);
  const [filtered, setFiltered] = useState<Protocol[]>([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const ref = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  useEffect(() => {
    api.get('/staking/options?limit=200')
      .then(res => {
        setProtocols(res.data.data?.options || []);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (!query.trim()) { setFiltered([]); setOpen(false); return; }
    const q = query.toLowerCase();
    const results = protocols
      .filter(p => p.name.toLowerCase().includes(q) || p.network?.toLowerCase().includes(q))
      .sort((a, b) => parseFloat(String(b.apy)) - parseFloat(String(a.apy)))
      .slice(0, 8);
    setFiltered(results);
    setOpen(results.length > 0);
  }, [query, protocols]);

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const handleSelect = (protocol: Protocol) => {
    setQuery('');
    setOpen(false);
    navigate(`/protocol/${protocol.slug}`);
  };

  const getCategoryColor = (category: string) => {
    if (category === 'native-staking') return 'text-green-400';
    if (category === 'liquid-staking') return 'text-yellow-400';
    return 'text-neon-red';
  };

  return (
    <div ref={ref} className="relative w-full max-w-2xl mx-auto">
      <div className="relative">
        <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-sky-blue/50" />
        <input
          type="text"
          value={query}
          onChange={e => setQuery(e.target.value)}
          onFocus={() => query && setOpen(true)}
          placeholder={loading ? 'Loading protocols...' : 'Search 156+ protocols — ETH, ATOM, SOL, DOT...'}
          className="w-full pl-12 pr-4 py-4 bg-steel-blue/10 border border-sky-blue/30 rounded-xl text-cream placeholder-sky-blue/40 focus:outline-none focus:border-neon-red transition-colors text-lg"
        />
      </div>

      {open && filtered.length > 0 && (
        <div className="absolute top-full left-0 right-0 mt-2 bg-navy-dark border border-sky-blue/30 rounded-xl shadow-card-hover z-50 overflow-hidden">
          <div className="px-4 py-2 border-b border-sky-blue/10">
            <span className="text-sky-blue/60 text-xs uppercase tracking-wider">
              {filtered.length} protocols found
            </span>
          </div>
          {filtered.map(p => {
            const apy = parseFloat(String(p.apy || 0));
            return (
              <button
                key={p.id}
                onClick={() => handleSelect(p)}
                className="w-full flex items-center justify-between px-4 py-3 hover:bg-steel-blue/20 transition-colors border-b border-sky-blue/5 last:border-0"
              >
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-lg bg-gradient-cta flex items-center justify-center text-white font-bold text-sm shadow-neon shrink-0">
                    {p.name[0]}
                  </div>
                  <div className="text-left">
                    <div className="text-cream font-semibold text-sm">{p.name}</div>
                    <div className="flex items-center gap-2">
                      <span className="text-sky-blue text-xs">{p.network}</span>
                      <span className={`text-xs ${getCategoryColor(p.category)}`}>
                        {p.category === 'native-staking' ? 'Native' : p.category === 'liquid-staking' ? 'Liquid' : 'DeFi'}
                      </span>
                    </div>
                  </div>
                </div>
                <div className="text-right">
                  <div className="flex items-center gap-1">
                    <TrendingUp className="w-3 h-3 text-neon-red" />
                    <span className="gradient-text-neon font-bold text-lg">{apy.toFixed(2)}%</span>
                  </div>
                  <div className="text-sky-blue/60 text-xs">APY</div>
                </div>
              </button>
            );
          })}
          <div className="px-4 py-3 bg-steel-blue/10 border-t border-sky-blue/10">
            <button
              onClick={() => { setOpen(false); navigate('/staking?q=' + query); }}
              className="text-sky-blue hover:text-neon-red transition-colors text-sm w-full text-left"
            >
              View all results for "{query}" →
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
